import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getQuickcamCoinUrl } from '../../../environments';
import { RootState } from '../../../store/create-store';
import { useUserData } from '../../contexts/user-data.context';
import {
  AvailabilityView,
  AvailableAction,
  QuickcamCoinWaitlistService,
} from '../../services/quickcam-coin-waitlist/quickcam-coin-waitlist.service';
import { evaluateQuickcamCoinLocation } from '../../services/quickcam-coin-waitlist/quickcam-coin-location-policy';
import {
  applyLocalLocationSim,
  ResolvedProfileLocation,
  resolveProfileLocation,
} from '../../services/quickcam-coin-waitlist/resolve-profile-location';

export type QccMenuCoinActionKind =
  | 'buy'
  | 'setLocation'
  | 'join'
  | 'onWaitlist'
  | 'joinLoading';

export const QCC_MENU_ACTION_LABEL: Record<QccMenuCoinActionKind, string> = {
  buy: 'Buy QuickCam Coins',
  setLocation: 'Set location',
  join: 'Join QCC Waiting List',
  joinLoading: 'Join QCC Waiting List',
  onWaitlist: 'On QCC Waiting List',
};

function isLoopbackHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]'
  );
}

export function resolveQccBuyUrl(envUrl?: string, token?: string): string {
  const raw = getQuickcamCoinUrl(envUrl);
  try {
    const parsed = new URL(raw);
    if (token && isLoopbackHost(parsed.hostname)) {
      parsed.searchParams.set('token', token);
    }
    return parsed.toString();
  } catch {
    const base = raw.endsWith('/') ? raw : `${raw}/`;
    return base;
  }
}

export function hasQccWaitlistAction(
  view: AvailabilityView | null,
  action: AvailableAction,
): boolean {
  return Boolean(view && (view.availableActions || []).includes(action));
}

export type QccMenuCoinAction = {
  kind: QccMenuCoinActionKind;
  profileLocation: ResolvedProfileLocation;
  canRejoin: boolean;
  buyUrl: string;
  joinWaitlist: () => Promise<void>;
  refreshAvailability: () => Promise<void>;
};

export function useQccMenuCoinAction(menuOpen = true): QccMenuCoinAction {
  const { user } = useUserData();
  const token = useSelector((state: RootState) => state.common.auth?.token);
  const [view, setView] = useState<AvailabilityView | null>(null);

  const profileLocation = useMemo(
    () =>
      applyLocalLocationSim(
        resolveProfileLocation(user),
        typeof window !== 'undefined' ? window.location.href : '',
      ),
    [user],
  );

  const locationDecision = useMemo(
    () =>
      evaluateQuickcamCoinLocation(
        profileLocation.countryCode,
        profileLocation.stateName,
      ),
    [profileLocation.countryCode, profileLocation.stateName],
  );

  const isIncomplete =
    locationDecision.restriction === 'LOCATION_INCOMPLETE';

  const refreshAvailability = useCallback(async () => {
    const next = await QuickcamCoinWaitlistService.getAvailability();
    setView(next);
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    let cancelled = false;
    QuickcamCoinWaitlistService.getAvailability().then(next => {
      if (!cancelled) {
        setView(next);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [token, menuOpen]);

  const joinWaitlist = useCallback(async () => {
    if (hasQccWaitlistAction(view, 'REJOIN_WAITLIST')) {
      await QuickcamCoinWaitlistService.rejoin();
    } else {
      await QuickcamCoinWaitlistService.join({
        inApp: true,
        email: false,
        sms: false,
        push: false,
      });
    }
    await refreshAvailability();
  }, [view, refreshAvailability]);

  let kind: QccMenuCoinActionKind = 'joinLoading';
  if (isIncomplete) {
    kind = 'setLocation';
  } else if (!view) {
    kind = 'joinLoading';
  } else if (hasQccWaitlistAction(view, 'BUY_QUICKCAM_COIN')) {
    kind = 'buy';
  } else if (hasQccWaitlistAction(view, 'LEAVE_WAITLIST')) {
    kind = 'onWaitlist';
  } else if (
    hasQccWaitlistAction(view, 'JOIN_WAITLIST') ||
    hasQccWaitlistAction(view, 'REJOIN_WAITLIST')
  ) {
    kind = 'join';
  }

  return {
    kind,
    profileLocation,
    canRejoin: hasQccWaitlistAction(view, 'REJOIN_WAITLIST'),
    buyUrl: resolveQccBuyUrl(undefined, token),
    joinWaitlist,
    refreshAvailability,
  };
}
